import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { MergePartDiff } from "~/api/types";
import { Button } from "~/foundation/components/Button";
import { Text } from "~/foundation/components/Text";
import { formatRelativeTime } from "~/foundation/utils/time";

/** ProseMirror-node-ish JSON to a plain string, well enough for a resolved-part preview. */
function textOf(content: unknown): string {
  if (!content || typeof content !== "object") {
    return "";
  }
  const node = content as { content?: unknown[]; text?: string };
  if (typeof node.text === "string") {
    return node.text;
  }
  if (Array.isArray(node.content)) {
    return node.content.map(textOf).join(" ");
  }
  return "";
}

export function MergePartRow({
  part,
  resolved,
  onResolve,
}: {
  part: MergePartDiff;
  resolved: { decision: "accept" | "reject" | "combine" } | undefined;
  onResolve: (
    decision: "accept" | "reject" | "combine",
    chosenFrom?: string,
    combinedContent?: string,
  ) => void;
}) {
  const { t, i18n } = useTranslation();
  const [combining, setCombining] = useState(false);
  const [combinedText, setCombinedText] = useState("");

  const needsDecision = part.conflict || part.sides.some((side) => side.changed);

  return (
    <li className="flex flex-col gap-2 rounded-md border border-divider p-3">
      <div className="flex items-center gap-2">
        <span className="text-small text-text-tertiary">{part.partType}</span>
        {part.conflict ? (
          <span className="rounded-full bg-danger/10 px-2 py-0.5 text-danger text-xs">
            {t("Conflict")}
          </span>
        ) : null}
        {resolved ? (
          <span className="rounded-full bg-success/10 px-2 py-0.5 text-success text-xs">
            {t("Resolved")}
          </span>
        ) : needsDecision ? (
          <span className="rounded-full bg-warning/10 px-2 py-0.5 text-warning text-xs">
            {t("Needs a decision")}
          </span>
        ) : null}
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {part.sides.map((side) => (
          <div
            key={`${part.partId}-${side.revisionId}`}
            className="rounded border border-divider p-2"
          >
            <div className="flex items-center justify-between text-small">
              <span className="text-text-secondary">
                {side.author.kind === "ai"
                  ? t("{{model}}", { model: side.author.aiModel ?? "AI" })
                  : t("A person")}
              </span>
              <span className="text-text-tertiary">
                {formatRelativeTime(side.createdAt, i18n.language)}
              </span>
            </div>
            <Text variant="small" className="mt-1">
              {side.changed
                ? textOf(side.content) || t("(changed, no preview available)")
                : t("Unchanged")}
            </Text>
            {needsDecision && !resolved ? (
              <Button
                size="sm"
                variant="ghost"
                className="mt-1"
                onClick={() => onResolve("accept", side.revisionId)}
              >
                {t("Accept this side")}
              </Button>
            ) : null}
          </div>
        ))}
      </div>

      {needsDecision && !resolved ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => onResolve("reject")}>
            {t("Keep base")}
          </Button>
          {combining ? (
            <div className="flex w-full flex-col gap-1.5">
              <textarea
                value={combinedText}
                onChange={(e) => setCombinedText(e.target.value)}
                rows={3}
                className="rounded-sm border border-input-border bg-bg p-2 text-body text-text"
              />
              <Text variant="small">
                {t("Combining keeps the text and drops block formatting")}
              </Text>
              <div>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={!combinedText.trim()}
                  onClick={() =>
                    onResolve(
                      "combine",
                      undefined,
                      JSON.stringify({
                        type: "paragraph",
                        content: [{ type: "text", text: combinedText }],
                      }),
                    )
                  }
                >
                  {t("Use this combined text")}
                </Button>
              </div>
            </div>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setCombining(true)}>
              {t("Combine")}
            </Button>
          )}
        </div>
      ) : null}
    </li>
  );
}
